import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Typography,
    Grid,
    CircularProgress,
    Alert,
    Snackbar,
    FormControlLabel,
    Switch,
    Divider
} from '@mui/material';
import { Settings as SettingsIcon, Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { getSiteConfig, getAppVersion, updateSiteConfig, updateAppVersion } from '../../../shared/services/utilityService';
import { SiteConfig, AppVersion } from '../../../shared/types/utility';

export const Settings: React.FC = () => {
    const [config, setConfig] = useState<SiteConfig | null>(null);
    const [version, setVersion] = useState<AppVersion | null>(null);
    const [loading, setLoading] = useState(true);
    const [updatingConfig, setUpdatingConfig] = useState(false);
    const [updatingOrdering, setUpdatingOrdering] = useState(false);
    const [updatingVersion, setUpdatingVersion] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const [configRes, versionRes] = await Promise.all([
                getSiteConfig(),
                getAppVersion()
            ]);
            setConfig(configRes.data);
            setVersion(versionRes.data);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to fetch settings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleConfigSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!config) return;

        try {
            setUpdatingConfig(true);
            const res = await updateSiteConfig({
                appName: config.appName,
                supportEmail: config.supportEmail,
                supportPhone: config.supportPhone
            });
            setSuccess(res.message || 'Site configuration updated successfully');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update site configuration');
        } finally {
            setUpdatingConfig(false);
        }
    };

    const handleOrderingSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!config) return;

        try {
            setUpdatingOrdering(true);
            const res = await updateSiteConfig({
                appName: config.appName,
                supportEmail: config.supportEmail,
                supportPhone: config.supportPhone,
                orderingEnabled: config.orderingEnabled,
                orderingStartTime: config.orderingStartTime,
                orderingEndTime: config.orderingEndTime,
                timezone: 'Asia/Kolkata'
            });
            setSuccess(res.message || 'Ordering time restriction updated successfully');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update ordering time restriction');
        } finally {
            setUpdatingOrdering(false);
        }
    };

    const handleVersionSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!version) return;

        try {
            setUpdatingVersion(true);
            const res = await updateAppVersion({
                minAppVersion: version.minAppVersion,
                latestAppVersion: version.latestAppVersion,
                forceUpdate: version.forceUpdate,
                updateMessage: version.updateMessage
            });
            setSuccess(res.message || 'App version updated successfully');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update app version');
        } finally {
            setUpdatingVersion(false);
        }
    };

    const handleCloseSnackbar = () => {
        setError(null);
        setSuccess(null);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div className="add-edit-admin-container">
            <div className="add-edit-admin-content">
                <div className="form-wrapper max-w-6xl">
                    <div className="flex items-center justify-between">
                        <div className="title text-2xl sm:text-5xl">Settings</div>
                        <AppButton
                            variant="secondary"
                            startIcon={<RefreshIcon />}
                            onClick={fetchData}
                            disabled={loading}
                        >
                            Refresh
                        </AppButton>
                    </div>

                    <div className="space-y-8">
                        {/* Site Configuration */}
                        <div className="form-card">
                            <div className="mb-6">
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    Site Configuration
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Manage your application's basic information and support details.
                                </Typography>
                            </div>
                            {config && (
                                <form onSubmit={handleConfigSubmit} className="space-y-4">
                                    <TextField
                                        label="App Name"
                                        fullWidth
                                        required
                                        value={config.appName}
                                        onChange={(e) => setConfig({ ...config, appName: e.target.value })}
                                    />
                                    <TextField
                                        label="Support Email"
                                        fullWidth
                                        required
                                        type="email"
                                        value={config.supportEmail}
                                        onChange={(e) => setConfig({ ...config, supportEmail: e.target.value })}
                                    />
                                    <TextField
                                        label="Support Phone"
                                        fullWidth
                                        required
                                        value={config.supportPhone}
                                        onChange={(e) => setConfig({ ...config, supportPhone: e.target.value })}
                                    />
                                    <div className="form-actions">
                                        <AppButton
                                            type="submit"
                                            variant="primary"
                                            startIcon={<SaveIcon />}
                                            loading={updatingConfig}
                                        >
                                            Save Configuration
                                        </AppButton>
                                    </div>
                                </form>
                            )}
                        </div>

                        {/* Ordering Time Restriction */}
                        <div className="form-card">
                            <div className="mb-6">
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    Ordering Time Restriction
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Control the hours during which customers can add to cart or place orders.
                                </Typography>
                            </div>
                            {config && (
                                <form onSubmit={handleOrderingSubmit} className="space-y-4">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={config.orderingEnabled}
                                                onChange={(e) => setConfig({ ...config, orderingEnabled: e.target.checked })}
                                                color="primary"
                                            />
                                        }
                                        label="Ordering Enabled"
                                    />
                                    <TextField
                                        label="Ordering Start Time"
                                        type="time"
                                        fullWidth
                                        required
                                        value={config.orderingStartTime}
                                        onChange={(e) => setConfig({ ...config, orderingStartTime: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <TextField
                                        label="Ordering End Time"
                                        type="time"
                                        fullWidth
                                        required
                                        value={config.orderingEndTime}
                                        onChange={(e) => setConfig({ ...config, orderingEndTime: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <TextField
                                        label="Timezone"
                                        fullWidth
                                        disabled
                                        value="Asia/Kolkata"
                                        helperText="Fixed to Asia/Kolkata"
                                    />
                                    <div className="form-actions">
                                        <AppButton
                                            type="submit"
                                            variant="primary"
                                            startIcon={<SaveIcon />}
                                            loading={updatingOrdering}
                                        >
                                            Save Ordering Settings
                                        </AppButton>
                                    </div>
                                </form>
                            )}
                        </div>

                        {/* App Version Info */}
                        <div className="form-card">
                            <div className="mb-6">
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    Version Information
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Control app updates and versioning settings.
                                </Typography>
                            </div>
                            {version && (
                                <form onSubmit={handleVersionSubmit} className="space-y-4">
                                    <TextField
                                        label="Minimum App Version"
                                        fullWidth
                                        required
                                        value={version.minAppVersion}
                                        onChange={(e) => setVersion({ ...version, minAppVersion: e.target.value })}
                                    />
                                    <TextField
                                        label="Latest App Version"
                                        fullWidth
                                        required
                                        value={version.latestAppVersion}
                                        onChange={(e) => setVersion({ ...version, latestAppVersion: e.target.value })}
                                    />
                                    <TextField
                                        label="Update Message"
                                        fullWidth
                                        required
                                        multiline
                                        rows={3}
                                        value={version.updateMessage}
                                        onChange={(e) => setVersion({ ...version, updateMessage: e.target.value })}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={version.forceUpdate}
                                                onChange={(e) => setVersion({ ...version, forceUpdate: e.target.checked })}
                                                color="primary"
                                            />
                                        }
                                        label="Force Update"
                                    />
                                    <div className="form-actions">
                                        <AppButton
                                            type="submit"
                                            variant="primary"
                                            startIcon={<SaveIcon />}
                                            loading={updatingVersion}
                                        >
                                            Save Version Info
                                        </AppButton>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>


            {/* Notifications */}
            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};

